import _ from 'lodash';
import update from 'immutability-helper';
import { handleActions } from 'redux-actions';
import ChannelActionCreators from '../actions/ChannelActionCreators';

const {
  fetchChannelRequest,
  fetchChannelSuccess,
  fetchChannelFailure,
  createChannelSuccess,
  ackChannelRead,
  setCurrentChannel,
  createChannelMembers,
  fetchChannelMembersRequest,
  fetchChannelMembersSuccess,
  fetchChannelMembersFailure,
  updateChannel,
  fetchPrevChannelMsgs,
  fetchTaggableMedicalRecordsRequest,
  fetchTaggableMedicalRecordsSuccess,
  fetchTaggableMedicalRecordsFailure,
  fetchChannelMsgsFailure,
  fetchChannelMsgsSuccess,
  fetchChannelMsgsRequest,
  createChannelMessage,
  uploadChannelAvatarRequest,
  uploadChannelAvatarSuccess,
  uploadChannelAvatarFailure,
  getInvitableUsersRequest,
  getInvitableUsersSuccess,
  getInvitableUsersFailure,
  deleteChannelMember,
  deleteChannel,
} = ChannelActionCreators;

const initialState = {
  currentChannelId: 0,
  channels: [],
  // object where every key is the channel id with an object
  // where key is a user id and has an object with the channel members detail
  membersInChannel: {},
  // Object where every key is the channel id with an array
  // where store messages which are sorted and fetched from backend
  msgsInChannel: {},
  isFetchingChannels: false,
  isFetchingChannelMembers: false,
  isFetchingChannelMsgs: false,
  taggableMedicalRecords: [],
  isFetchingTaggableMedicalRecords: false,
  isUploadingChannelAvatar: false,
  invitableUsers: [],
  isGettingInvitableUsers: false,
};

const channel = handleActions({
  [setCurrentChannel]: (state, { payload }) =>
    update(state, { currentChannelId: { $set: payload } }),

  [fetchChannelRequest]: state =>
    update(state, { isFetchingChannels: { $set: true } }),

  [fetchChannelFailure]: state =>
    update(state, { isFetchingChannels: { $set: false } }),

  [fetchChannelSuccess]: (state, { payload }) =>
    update(state, {
      isFetchingChannels: { $set: false },
      channels: { $set: payload },
    }),

  [createChannelSuccess]: (state, { payload }) =>
    update(state, { channels: { $push: [payload] } }),

  [ackChannelRead]: (state, { payload }) => {
    const { channelId, userId } = payload;
    let newState;
    if (!state.membersInChannel[channelId]) {
      newState = update(state, {
        membersInChannel: {
          $merge: {
            [channelId]: { [userId]: payload },
          },
        },
      });
    } else {
      newState = update(state, {
        membersInChannel: {
          [channelId]: { $merge: { [userId]: payload } },
        },
      });
    }
    return newState;
  },

  [fetchChannelMembersRequest]: state =>
    update(state, { isFetchingChannelMembers: { $set: true } }),

  [fetchChannelMembersFailure]: state =>
    update(state, { isFetchingChannelMembers: { $set: false } }),

  [fetchChannelMembersSuccess]: (state, { payload }) => {
    const { members, channelId } = payload;
    const channelMembersByUserId = _.reduce(members,
      (result, cm) => ({ ...result, [cm.userId]: cm }), {});
    return update(state, {
      isFetchingChannelMembers: { $set: false },
      membersInChannel: { [channelId]: { $set: channelMembersByUserId } },
    });
  },

  [fetchChannelMsgsRequest]: state =>
    update(state, { isFetchingChannelMsgs: { $set: true } }),

  [fetchChannelMsgsFailure]: state =>
    update(state, { isFetchingChannelMsgs: { $set: false } }),

  [fetchChannelMsgsSuccess]: (state, { payload }) => {
    const { channelId, msgs: newMsgs } = payload;
    const msgs = state.msgsInChannel[channelId];
    const needAppendedMsgs = _.filter(newMsgs, m => _.findIndex(msgs, { id: m.id }) === -1);

    if (_.isEmpty(state.msgsInChannel[channelId])) {
      return update(state, {
        isFetchingChannelMsgs: { $set: false },
        msgsInChannel: { [channelId]: { $set: needAppendedMsgs } },
      });
    }

    return update(state, {
      isFetchingChannelMsgs: { $set: false },
      msgsInChannel: { [channelId]: { $push: needAppendedMsgs } },
    });
  },

  [fetchPrevChannelMsgs]: (state, { payload }) => {
    const { channelId, msgs } = payload;
    const sortedMsgs = _.sortBy(msgs, ['seq']);
    const combinedMsg = [...sortedMsgs, ...state.msgsInChannel[channelId]];
    // NOTE:
    // found that using react-addon-update helper $unshift would cause
    // unexpected result. So use this simple spread operator to prepend items
    // into array. However, this might have the performance issue when array is
    // too big so this should be refactored in the future.
    return update(state, {
      msgsInChannel: { [channelId]: { $set: combinedMsg } },
    });
  },
  [createChannelMessage]: (state, { payload }) => {
    const { channelId, msg } = payload;

    return update(state, {
      msgsInChannel: { [channelId]: { $push: [msg] } },
    });
  },
  [createChannelMembers]: (state, { payload }) => {
    const { channelId } = payload[0];
    const formattedMembers = payload.reduce(
      (result, member) => ({ ...result, [member.userId]: member }), {});

    return update(state, {
      membersInChannel: { [channelId]: { $merge: formattedMembers } },
    });
  },

  /**
   * Taggable Medical Records
   */
  [fetchTaggableMedicalRecordsRequest]: state => (
    update(state, { isFetchingTaggableMedicalRecords: { $set: true } })
  ),
  [fetchTaggableMedicalRecordsSuccess]: (state, { payload }) => (
    update(state, {
      isFetchingTaggableMedicalRecords: { $set: false },
      taggableMedicalRecords: { $set: payload },
    })
  ),
  [fetchTaggableMedicalRecordsFailure]: state => (
    update(state, { isFetchingTaggableMedicalRecords: { $set: false } })
  ),

  /**
   *  Upload Channel Avatar
   */
  [uploadChannelAvatarRequest]: state => (
    update(state, { isUploadingChannelAvatar: { $set: true } })
  ),
  [uploadChannelAvatarSuccess]: (state, { payload }) => {
    const idx = _.findIndex(state.channels, { id: payload.channelId });
    return update(state, {
      isUploadingChannelAvatar: { $set: false },
      channels: { [idx]: { $merge: { avatar: payload.avatar } } },
    });
  },
  [uploadChannelAvatarFailure]: state => (
    update(state, { isUploadingChannelAvatar: { $set: false } })
  ),

  [updateChannel]: (state, { payload }) => {
    const { channelId, ...data } = payload;
    const idx = _.findIndex(state.channels, { id: channelId });

    return update(state, { channels: { [idx]: { $merge: data } } });
  },

  [deleteChannelMember]: (state, { payload }) => (
    update(state, {
      membersInChannel: { [payload.channelId]: { $unset: [payload.userId] } },
    })
  ),

  [deleteChannel]: (state, { payload }) => {
    const idx = _.findIndex(state.channels, { id: payload });

    return update(state, { channels: { $splice: [[idx, 1]] } });
  },

  [getInvitableUsersRequest]: state => (
    update(state, { isGettingInvitableUsers: { $set: true } })
  ),
  [getInvitableUsersSuccess]: (state, { payload }) => (
    update(state, {
      isGettingInvitableUsers: { $set: false },
      invitableUsers: { $set: payload },
    })
  ),
  [getInvitableUsersFailure]: state => (
    update(state, { isGettingInvitableUsers: { $set: false } })
  ),
}, initialState);

export default channel;



// WEBPACK FOOTER //
// ./src/reducers/channel.js